import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type UserRef } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDays, formatNumber } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';

/** Routes: `/departments/new` and `/departments/:id/edit`. */
export function DepartmentFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['department', id],
    queryFn: () => api.get<any>(`/departments/${id}`),
    enabled: editing,
  });

  // A field left empty takes the company default, so its placeholder names the figure.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });
  const inherits = (key: string, show: (value: number) => string) => {
    const value = settings.data?.sla?.[key];
    return value === null || value === undefined
      ? t('departments.inherit')
      : t('departments.companyDefaultIs', { value: show(Number(value)) });
  };
  const minutes = (value: number) => formatNumber(value, locale);
  const days = (value: number) => formatDays(value, locale);

  const [form, setForm] = useState({
    name: '',
    nameAr: '',
    code: '',
    description: '',
    viewSlaMinutes: '' as string | number,
    assignmentSlaMinutes: '' as string | number,
    viewPenaltyDays: '' as string | number,
    assignmentPenaltyDays: '' as string | number,
  });

  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const d = existing.data;
    setForm({
      name: d.name ?? '',
      nameAr: d.nameAr ?? '',
      code: d.code ?? '',
      description: d.description ?? '',
      viewSlaMinutes: d.viewSlaMinutes ?? '',
      assignmentSlaMinutes: d.assignmentSlaMinutes ?? '',
      // Decimal columns arrive as strings; "0.50" reads better as 0.5.
      viewPenaltyDays: d.viewPenaltyDays == null ? '' : Number(d.viewPenaltyDays),
      assignmentPenaltyDays: d.assignmentPenaltyDays == null ? '' : Number(d.assignmentPenaltyDays),
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: form.name,
        nameAr: form.nameAr || undefined,
        code: form.code,
        description: form.description || undefined,
        // An empty field means "inherit", which is null on the wire — not zero.
        viewSlaMinutes: form.viewSlaMinutes === '' ? null : Number(form.viewSlaMinutes),
        assignmentSlaMinutes: form.assignmentSlaMinutes === '' ? null : Number(form.assignmentSlaMinutes),
        viewPenaltyDays: form.viewPenaltyDays === '' ? null : Number(form.viewPenaltyDays),
        assignmentPenaltyDays: form.assignmentPenaltyDays === '' ? null : Number(form.assignmentPenaltyDays),
      };
      return editing ? api.patch<any>(`/departments/${id}`, body) : api.post<any>('/departments', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      void queryClient.invalidateQueries({ queryKey: ['department'] });
      navigate(`/departments/${saved.id ?? id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${form.code}` : t('departments.new')}
      backTo={editing ? `/departments/${id}` : '/departments'}
      backLabel={t('nav.departments')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!form.name || !form.code}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.code')} required>
          <Input
            dir="ltr"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label={t('common.description')} className="sm:col-span-2">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>

        <p className="mt-2 border-t hairline pt-3 text-body-sm font-medium text-[var(--text-secondary)] sm:col-span-2">
          {t('departments.slaOverrides')}
          <span className="ms-2 text-caption font-normal text-faint">{t('departments.inheritNote')}</span>
        </p>

        <Field label={t('settings.viewSlaMinutes')}>
          <Input
            type="number"
            min={1}
            value={form.viewSlaMinutes}
            onChange={(e) => setForm({ ...form, viewSlaMinutes: e.target.value })}
            placeholder={inherits('sla.viewSlaMinutes', minutes)}
          />
        </Field>
        <Field label={t('settings.assignmentSlaMinutes')}>
          <Input
            type="number"
            min={1}
            value={form.assignmentSlaMinutes}
            onChange={(e) => setForm({ ...form, assignmentSlaMinutes: e.target.value })}
            placeholder={inherits('sla.assignmentSlaMinutes', minutes)}
          />
        </Field>
        <Field label={t('settings.viewPenaltyDays')} hint={t('departments.penaltyDaysHint')}>
          <Input
            type="number"
            min={0}
            max={30}
            step={0.25}
            value={form.viewPenaltyDays}
            onChange={(e) => setForm({ ...form, viewPenaltyDays: e.target.value })}
            placeholder={inherits('sla.viewPenaltyDays', days)}
          />
        </Field>
        <Field label={t('settings.assignmentPenaltyDays')} hint={t('departments.penaltyDaysHint')}>
          <Input
            type="number"
            min={0}
            max={30}
            step={0.25}
            value={form.assignmentPenaltyDays}
            onChange={(e) => setForm({ ...form, assignmentPenaltyDays: e.target.value })}
            placeholder={inherits('sla.assignmentPenaltyDays', days)}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/departments/:id/members/new`. */
export function DepartmentMemberFormPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [memberType, setMemberType] = useState('MEMBER');

  const department = useQuery({
    queryKey: ['department', id],
    queryFn: () => api.get<any>(`/departments/${id}`),
  });
  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });

  const add = useMutation({
    mutationFn: () => api.post(`/departments/${id}/members`, { userId, memberType }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['department', id] });
      navigate(`/departments/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('departments.members')}
      subtitle={department.data?.name}
      backTo={`/departments/${id}`}
      backLabel={department.data?.code ?? t('nav.departments')}
      submitLabel={t('common.add')}
      submitting={add.isPending}
      disabled={!userId}
      loading={department.isLoading}
      error={department.error}
      onRetry={() => void department.refetch()}
      onSubmit={() => add.mutate()}
    >
      <FormGrid>
        <Field label={t('common.name')} required>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus>
            <option value="">—</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>{localized(u)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.role')}>
          <Select value={memberType} onChange={(e) => setMemberType(e.target.value)}>
            <option value="MEMBER">{enumLabel('MEMBER', locale)}</option>
            <option value="LEADER">{enumLabel('LEADER', locale)}</option>
          </Select>
        </Field>
      </FormGrid>
    </FormPage>
  );
}
